/**
 * PanelDropdown — the light-card select. Opens UPWARD: the panel lives at the bottom of the
 * screen, so a list dropping down would leave the viewport.
 *
 * Sibling of the debug panel's Dropdown, deliberately not shared with it — that one is part of the
 * dark tooling theme and carries multi-select behaviour this never needs. The list itself is
 * OptionList, shared with PanelStepper.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { CONTROL_H, TRACK, MOTION } from './tokens';
import { OptionList, type OptionItem } from './OptionList';

interface PanelDropdownProps<T> {
  value: T | null;
  options: readonly OptionItem<T>[];
  onSelect: (value: T) => void;
  /** Per-item delete affordance. Hidden when only one option remains. */
  onDelete?: (value: T) => void;
  /** Extra row pinned to the bottom of the list (e.g. "New World"). */
  action?: { label: string; icon?: ReactNode; onClick: () => void };
  placeholder?: string;
  ariaLabel?: string;
}

export function PanelDropdown<T extends string | number>({
  value, options, onSelect, onDelete, action, placeholder = 'Select', ariaLabel,
}: PanelDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [open]);

  const current = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`w-full ${CONTROL_H} ${TRACK} px-4 flex items-center justify-between gap-2
          text-[13px] md:text-xs font-bold text-neutral-800 cursor-pointer hover:bg-neutral-200/70 ${MOTION}`}
      >
        <span className="truncate">{current?.label ?? placeholder}</span>
        <ChevronDown size={16} className={`shrink-0 text-neutral-400 ${MOTION} ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <OptionList
          options={options}
          value={value}
          onSelect={onSelect}
          onDelete={onDelete}
          action={action}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
